import { useEffect, useState } from 'react';
import { Box, Button, IconButton, List, ListItem, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useDispatch, useSelector } from 'react-redux';
import { getSimulatorProfiles, setSimulatorProfiles } from '../../../redux/simulatorProfilesSlice';
import PolicySimulator from '../../../utils/PolicySimulator.ts';
import sortedProfilesAttributes from '../../../utils/sortedProfilesAttributes';
import { setSnackbarMessage } from '../../../redux/snackbarMessageSlice';
import UserProfileManagement from '../Regulatory/components/UserProfileManagement';
import { getSubjectSchema } from '../../../redux/subjectSchemaSlice';
import { getSelectedProfileSimulate, setSelectedProfileSimulate } from '../../../redux/selectedProfileSimulateSlice';
import { getOpenSimulatorPolicy } from '../../../redux/openSimulatorPolicySlice';
import { dataTypes } from '../../../utils/configDataTypes';
import { v4 as uuidv4 } from 'uuid';
import ConfirmDialogReuse from '../ReuseComponents/ConfirmDialogReuse';
import { LoadingButton } from '@mui/lab';
import CustomProfilesPolicySimulator from './components/CustomProfilesPolicySimulator';
import findSubjectAttrToSimulator from '../../../utils/findSubjectAttrToSimulator.js';
import TuneIcon from '@mui/icons-material/Tune';
import { ProfileBuilderManagement } from './components/ProfileBuilderManagement.js';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { getRuleByPolicyDiagram } from '../../../utils/convertPolicyStructure.js';
import { getIAAM } from '../../../redux/iaamSlice.js';

const POLICY_PROFILE_BUILDER_STORAGE_KEY = 'policy-simulation-profile-builder';
const PROFILE_BUILDER_CHANGED_EVENT = 'policy-simulation-profile-builder-changed';
const POLICY_FILE_CHANGED_EVENT = 'policy-file-changed';
const POLICY_FILE_UPDATE_STATUS_KEY = 'policy-file-update-status';

const profile_builder = {
    policy_diagram_id: [
        {
            dataKey: 'application',
            label: 'Application Profile',
            profiles: []
        },
        {
            dataKey: 'restful_api',
            label: 'RESTful-API Profile',
            profiles: []
        },
        {
            dataKey: 'data',
            label: 'Data Profile',
            profiles: []
        },
    ],
};

const createDefaultBuilderSections = () => (
    profile_builder.policy_diagram_id.map((section) => ({
        ...section,
        profiles: [],
    }))
);

const defaultSectionAvailability = {
    application: true,
    restful_api: true,
    data: true,
};

const mapRuleTypeToSectionKey = {
    WEB: 'application',
    NATIVE: 'application',
    API: 'restful_api',
    DATA: 'data',
};

const normalizeNullableField = (value) => {
    if (value === undefined || value === null) {
        return null;
    }

    const normalized = String(value).trim();
    return normalized.length > 0 ? normalized : null;
};

const sanitizeBuilderProfilePayload = (data = {}, dataKey = '') => {
    const payload = {
        ...data,
        profile_name: String(data?.profile_name || '').trim(),
        environment_resource_name: String(data?.environment_resource_name || '').trim(),
        path: data?.path,
        ip_address: normalizeNullableField(data?.ip_address),
        date_time: normalizeNullableField(data?.date_time),
    };

    if (Object.prototype.hasOwnProperty.call(payload, 'values')) {
        delete payload.values;
    }

    if (Object.prototype.hasOwnProperty.call(payload, 'action')) {
        payload.action = Array.isArray(payload.action)
            ? payload.action.filter((action) => String(action || '').trim().length > 0)
            : [];
    }

    if (dataKey === 'application') {
        delete payload.action;
        delete payload.environment_resource_name;
        delete payload.path;
    }

    return payload;
};

const normalizeProfileRow = (profile, dataKey = '') => {
    const normalized = {
        id: profile?.id || profile?.profile_id || '',
        profile_id: profile?.profile_id || profile?.id || '',
        profile_name: profile?.profile_name || '',
        path: String(profile?.path || '').trim(),
        environment_resource_name: String(profile?.environment_resource_name || '').trim(),
        ip_address: normalizeNullableField(profile?.ip_address),
        date_time: normalizeNullableField(profile?.date_time),
    };

    if (dataKey !== 'application') {
        normalized.action = Array.isArray(profile?.action) ? profile.action : [];
    }

    return normalized;
};

const normalizeBuilderSections = (entry) => {
    const defaults = createDefaultBuilderSections();

    // New shape: store[policy_diagram_id] = [{ dataKey, label, profiles: [{ profile_id, profile_name, values }] }]
    if (Array.isArray(entry)) {
        return defaults.map((defaultSection) => {
            const matched = entry.find((section) => section?.dataKey === defaultSection.dataKey);
            const matchedProfiles = Array.isArray(matched?.profiles)
                ? matched.profiles.map((profile) => normalizeProfileRow(profile, defaultSection.dataKey))
                : [];
            return {
                dataKey: defaultSection.dataKey,
                label: matched?.label || defaultSection.label,
                profiles: matchedProfiles,
            };
        });
    }

    // Legacy shape: store[policy_diagram_id] = { profiles: { [profileId]: { profile_name, application, restful_api, data } } }
    if (entry && typeof entry === 'object' && entry.profiles && typeof entry.profiles === 'object') {
        const profileRows = Object.values(entry.profiles);
        return defaults.map((defaultSection) => ({
            dataKey: defaultSection.dataKey,
            label: defaultSection.label,
            profiles: profileRows.map((row) => ({
                profile_id: row?.profile_id || '',
                profile_name: row?.profile_name || '',
            })),
        }));
    }

    return defaults;
};

export const ProfilePanel = ({ nodes }) => {
    const dispatch = useDispatch();

    const simulatorProfiles = useSelector(getSimulatorProfiles);
    const subjectSchema = useSelector(getSubjectSchema);
    const selectedProfileSimulate = useSelector(getSelectedProfileSimulate);
    const openSimulatorPolicy = useSelector(getOpenSimulatorPolicy);
    const iaam = useSelector(getIAAM);

    const [profiles, setProfiles] = useState([]);
    const [project, setProject] = useState();
    const [profile, setProfile] = useState(null);
    const [policySimulator, setPolicySimulator] = useState(null);
    const [isProfileChanged, setIsProfileChanged] = useState(false);
    const [selectedEditProfile, setSelectedEditProfile] = useState(null);
    const [openManagement, setOpenManagement] = useState(false);
    const [memoizedAttributes, setMemoizedAttributes] = useState({});
    const [selectedDeleteProfile, setSelectedDeleteProfile] = useState(null);
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [policyDiagramId, setPolicyDiagramId] = useState('');
    const [builderStore, setBuilderStore] = useState({});
    const [openProfileBuilderManagement, setOpenProfileBuilderManagement] = useState(false);
    const [eventProfileBuilder, setEventProfileBuilder] = useState({});
    const [builderItemMenuAnchorEl, setBuilderItemMenuAnchorEl] = useState(null);
    const [selectedBuilderItemContext, setSelectedBuilderItemContext] = useState(null);

    const [expandedSections, setExpandedSections] = useState({});
    const [sectionAvailability, setSectionAvailability] = useState(defaultSectionAvailability);
    const [policyFileChangedToken, setPolicyFileChangedToken] = useState(0);

    useEffect(() => {
        const handlePolicyFileChanged = () => {
            setPolicyFileChangedToken((prev) => prev + 1);
        };

        window.addEventListener(POLICY_FILE_CHANGED_EVENT, handlePolicyFileChanged);

        return () => {
            window.removeEventListener(POLICY_FILE_CHANGED_EVENT, handlePolicyFileChanged);
        };
    }, []);

    useEffect(() => {
        const file = JSON.parse(localStorage.getItem('policy-file'));
        const currentPolicyDiagramId = file?.policy_diagram_id || '';

        const rawStore = localStorage.getItem(POLICY_PROFILE_BUILDER_STORAGE_KEY);
        let parsedStore = {};

        if (rawStore) {
            try {
                const maybeStore = JSON.parse(rawStore);
                parsedStore = (maybeStore && typeof maybeStore === 'object' && !Array.isArray(maybeStore)) ? maybeStore : {};
            } catch (error) {
                parsedStore = {};
            }
        }

        let nextStore = parsedStore;

        if (currentPolicyDiagramId) {
            if (!rawStore) {
                // Initialize storage with the requested structure keyed by policy_diagram_id.
                nextStore = {
                    [currentPolicyDiagramId]: createDefaultBuilderSections(),
                };
                localStorage.setItem(POLICY_PROFILE_BUILDER_STORAGE_KEY, JSON.stringify(nextStore));
            } else if (!Array.isArray(parsedStore[currentPolicyDiagramId])) {
                // Storage exists, but this policy key does not. Add default sections for this key.
                nextStore = {
                    ...parsedStore,
                    [currentPolicyDiagramId]: createDefaultBuilderSections(),
                };
                localStorage.setItem(POLICY_PROFILE_BUILDER_STORAGE_KEY, JSON.stringify(nextStore));
            }
        }

        setBuilderStore(nextStore);

        const initialProfile = async () => {
            setProfile(await iaam.getProfile());
        }
        if (file) {
            setPolicySimulator(new PolicySimulator(file))
            setPolicyDiagramId(file.policy_diagram_id || '');
            initialProfile();
        }
        const project = JSON.parse(localStorage.getItem('project'));
        if (project) {
            setProject(project);
        }
    }, [])

    useEffect(() => {
        const usedSubjectAttributes = findSubjectAttrToSimulator(nodes || []);
        setMemoizedAttributes(usedSubjectAttributes || {});

        if (!nodes || nodes.length === 0) {
            setProfiles([]);
            return;
        }

        if (!simulatorProfiles || simulatorProfiles.length === 0) {
            setProfiles([]);
            return;
        }

        // const hasUsedSubjectAttributes = Object.keys(usedSubjectAttributes || {}).length > 0;
        // if (!hasUsedSubjectAttributes) {
        //     setProfiles([]);
        //     return;
        // }

        const profilesData = simulatorProfiles.map(profile => {
            let attributes = {};
            Object.keys(usedSubjectAttributes).forEach(key => {
                attributes[key] = profile?.attributes[key] || null;
            });
            return {
                ...profile,
                attributes: attributes,
            };
        });
        setProfiles(profilesData);
    }, [simulatorProfiles, nodes]);

    const hasUsedSubjectAttributes = Object.keys(memoizedAttributes || {}).length > 0;
    const currentPolicyBuilderSections = normalizeBuilderSections(builderStore[policyDiagramId]);

    useEffect(() => {
        let isActive = true;

        const syncSectionAvailabilityFromRules = async () => {
            const updateStatus = localStorage.getItem(POLICY_FILE_UPDATE_STATUS_KEY);
            if (updateStatus === 'pending') {
                return;
            }

            const policyFile = JSON.parse(localStorage.getItem('policy-file') || 'null');
            if (!policyFile) {
                if (isActive) {
                    setSectionAvailability({
                        application: false,
                        restful_api: false,
                        data: false,
                    });
                }
                return;
            }

            try {
                const rules = await getRuleByPolicyDiagram(policyFile);
                const nextAvailability = {
                    application: false,
                    restful_api: false,
                    data: false,
                };

                (Array.isArray(rules) ? rules : []).forEach((item) => {
                    const ruleType = String(item?.resource?.type || '').toUpperCase();
                    const sectionKey = mapRuleTypeToSectionKey[ruleType];

                    if (sectionKey) {
                        nextAvailability[sectionKey] = true;
                    }
                });

                if (isActive) {
                    setSectionAvailability(nextAvailability);
                }
            } catch (error) {
                if (isActive) {
                    setSectionAvailability({
                        application: false,
                        restful_api: false,
                        data: false,
                    });
                }
            }
        };

        if (openSimulatorPolicy) {
            syncSectionAvailabilityFromRules();
        }

        return () => {
            isActive = false;
        };
    }, [policyDiagramId, openSimulatorPolicy, policyFileChangedToken]);

    useEffect(() => {
        setExpandedSections((prev) => {
            const nextExpanded = { ...prev };
            let hasChange = false;

            currentPolicyBuilderSections.forEach((section) => {
                const sectionKey = section.dataKey;
                const isEnabled = sectionAvailability[sectionKey] !== false;

                if (!Object.prototype.hasOwnProperty.call(nextExpanded, sectionKey)) {
                    nextExpanded[sectionKey] = isEnabled;
                    hasChange = true;
                    return;
                }

                if (!isEnabled && nextExpanded[sectionKey] !== false) {
                    nextExpanded[sectionKey] = false;
                    hasChange = true;
                }
            });

            return hasChange ? nextExpanded : prev;
        });
    }, [currentPolicyBuilderSections, sectionAvailability]);

    const toggleSection = (sectionId) => {
        setExpandedSections((prev) => ({
            ...prev,
            [sectionId]: !prev[sectionId],
        }));
    };

    const onProfileChanged = (value) => {
        setIsProfileChanged(value)
    };

    const onSelectedProfile = async (profile) => {
        return;
    }

    const onSaveProfile = async (profile) => {
        try {
            const profiles = simulatorProfiles.map(pf => {
                if (pf.id === profile.id) {
                    const updatedAttr = { ...pf.attributes, ...profile.attributes };
                    const updatedProfile = {
                        ...pf,
                        name: profile.name,
                        attributes: updatedAttr,
                        updated_at: profile.updated_at
                    }
                    onSelectedProfile(updatedProfile);
                    return updatedProfile
                }
                return pf;
            });
            // console.log("profiles:", profiles)
            const response = await fetch(`${process.env.REACT_APP_API_SIM_URL}/projects/${project?.project_id}/profiles`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    profiles: profiles
                })
            })
            if (response.ok) {
                // console.log("saved profile.", response)
                dispatch(setSimulatorProfiles(sortedProfilesAttributes(profiles)));
            } else {
                throw response;
            }
        } catch (error) {
            console.error("Error save profile: ", error);
            dispatch(setSnackbarMessage({ message: 'Failed to saved profile.', status: 'error' }));
        }
    };

    const handleEditProfile = (profile) => {
        const selectedProfile = simulatorProfiles.find(pf => pf.id === profile.id);
        setSelectedEditProfile(selectedProfile);
        setOpenManagement(true);
    };

    const createNewProfileName = () => {
        const hasUnnamed = simulatorProfiles.some(profile => profile.name === "unname");
        if (!hasUnnamed) {
            return "unname";
        }

        let counter = 1;
        let newName = `unname-${counter}`;

        while (simulatorProfiles.some(profile => profile.name === newName)) {
            counter++;
            newName = `unname-${counter}`;
        }

        return newName;
    };

    const handleAddProfile = async (newProfile) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_SIM_URL}/projects/${project?.project_id}/profiles`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(newProfile)
            });

            if (response.ok) {
                const result = await response.json();
                const updatedProfiles = [...simulatorProfiles, newProfile];
                dispatch(setSimulatorProfiles(sortedProfilesAttributes(updatedProfiles) || []));
                setSelectedEditProfile(newProfile);
                return;
            } else {
                throw new Error("Failed to add simulator profile");
            }
        } catch (error) {
            console.error(error.message);
            dispatch(setSnackbarMessage({
                open: true,
                message: error.message,
                status: "error",
            }));
        }
    };

    const onAddNewProfile = async () => {
        let usedAttributes = {};
        for (const attr of subjectSchema) {
            const is_type = dataTypes.find(dt => dt.similar_type.includes(attr.attributeType));
            if (is_type.type === 'boolean') {
                usedAttributes[attr.name] = false;
            } else if (is_type.type === 'array') {
                usedAttributes[attr.name] = [];
            } else {
                usedAttributes[attr.name] = null;
            }
        }

        const newProfile = {
            id: uuidv4(),
            name: createNewProfileName(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            attributes: usedAttributes,
        };

        await handleAddProfile(newProfile);
    };

    const createCloneProfileName = (originalName) => {
        let counter = 1;
        let newName = `${originalName}-copy${counter}`;

        while (simulatorProfiles.some(profile => profile.name === newName)) {
            counter++;
            newName = `${originalName}-copy${counter}`;
        }

        return newName;
    };

    const onCloneProfile = async (profile) => {
        const attributes = simulatorProfiles.find(pf => pf.id === profile.id).attributes || {};

        const newProfile = {
            id: uuidv4(),
            name: createCloneProfileName(profile.name),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            attributes: attributes,
        };

        await handleAddProfile(newProfile);
    };

    const handleDeleteProfile = (profile) => {
        setSelectedDeleteProfile(profile);
        setOpenDeleteConfirm(true);
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            let options = {
                method: 'DELETE',
                headers: {
                    'Content-type': 'application/json',
                },
                body: JSON.stringify({
                    profile_ids: [selectedDeleteProfile.id]
                })
            }
            const response = await fetch(`${process.env.REACT_APP_API_SIM_URL}/projects/${project?.project_id}/profiles`, options);
            if (response.ok) {
                const updatedProfiles = simulatorProfiles.filter(profile => profile.id !== selectedDeleteProfile.id);
                const notHaveSelected = !updatedProfiles.find(profile => profile.id === selectedProfileSimulate?.id);
                if (notHaveSelected) {
                    dispatch(setSelectedProfileSimulate(null));
                }
                dispatch(setSimulatorProfiles(updatedProfiles));
                setLoading(false);
                setOpenDeleteConfirm(false);
            } else {
                throw response;
            }
        } catch (error) {
            setLoading(false);
            setOpenDeleteConfirm(false);
            console.error("Error save profile: ", error);
            dispatch(setSnackbarMessage({ message: 'Failed to saved profile.', status: 'error' }));
        }
    };

    const onDeleteClose = () => {
        setOpenDeleteConfirm(false);
        setLoading(false);
    };

    const handleOpenProfileManagement = () => {
        setSelectedEditProfile(null);
        setOpenManagement(true);
    };

    const handleProfileBuilderEvent = (label, action, dataKey, item = null) => {
        if (action === 'create') {
            setEventProfileBuilder({
                action: action,
                title: `Create ${label}`,
                dataKey: dataKey,
                item: null,
            });
        } else {
            setEventProfileBuilder({
                action: action,
                title: `Update ${label}`,
                dataKey: dataKey,
                item: item,
            });
        }
        setOpenProfileBuilderManagement(true);
    };

    const handleOpenBuilderItemMenu = (event, section, item) => {
        setBuilderItemMenuAnchorEl(event.currentTarget);
        setSelectedBuilderItemContext({ section, item });
    };

    const handleCloseBuilderItemMenu = () => {
        setBuilderItemMenuAnchorEl(null);
        setSelectedBuilderItemContext(null);
    };

    const handleEditBuilderItem = () => {
        if (!selectedBuilderItemContext?.section || !selectedBuilderItemContext?.item) {
            handleCloseBuilderItemMenu();
            return;
        }

        const { section, item } = selectedBuilderItemContext;
        handleCloseBuilderItemMenu();
        handleProfileBuilderEvent(section.label, 'update', section.dataKey, item);
    };

    const handleDeleteBuilderItem = () => {
        const selectedSection = selectedBuilderItemContext?.section;
        const selectedItem = selectedBuilderItemContext?.item;
        if (!selectedSection || !selectedItem) {
            handleCloseBuilderItemMenu();
            return;
        }

        const file = JSON.parse(localStorage.getItem('policy-file') || '{}');
        const currentPolicyDiagramId = policyDiagramId || file?.policy_diagram_id;
        if (!currentPolicyDiagramId) {
            handleCloseBuilderItemMenu();
            return;
        }

        const store = readBuilderStore();
        const currentSections = Array.isArray(store[currentPolicyDiagramId])
            ? store[currentPolicyDiagramId].map((section) => ({
                ...section,
                profiles: Array.isArray(section?.profiles) ? [...section.profiles] : [],
            }))
            : null;

        if (!currentSections) {
            handleCloseBuilderItemMenu();
            return;
        }

        const targetSectionIndex = currentSections.findIndex((section) => section?.dataKey === selectedSection.dataKey);
        if (targetSectionIndex < 0) {
            handleCloseBuilderItemMenu();
            return;
        }

        const selectedId = selectedItem?.profile_id || selectedItem?.id;
        currentSections[targetSectionIndex].profiles = currentSections[targetSectionIndex].profiles.filter((profile) => {
            if (selectedId) {
                const profileId = profile?.profile_id || profile?.id;
                return profileId !== selectedId;
            }

            return String(profile?.profile_name || '') !== String(selectedItem?.profile_name || '');
        });

        const nextStore = {
            ...store,
            [currentPolicyDiagramId]: currentSections,
        };

        localStorage.setItem(POLICY_PROFILE_BUILDER_STORAGE_KEY, JSON.stringify(nextStore));
        setBuilderStore(nextStore);
        window.dispatchEvent(new CustomEvent(PROFILE_BUILDER_CHANGED_EVENT));
        handleCloseBuilderItemMenu();
    };

    const readBuilderStore = () => {
        const rawStore = localStorage.getItem(POLICY_PROFILE_BUILDER_STORAGE_KEY);
        if (!rawStore) {
            return {};
        }

        try {
            const parsedStore = JSON.parse(rawStore);
            return (parsedStore && typeof parsedStore === 'object' && !Array.isArray(parsedStore)) ? parsedStore : {};
        } catch (error) {
            return {};
        }
    };

    const onSubmitProfileBuilder = async (data, event) => {
        if (!data || !event?.dataKey) {
            return false;
        }

        const file = JSON.parse(localStorage.getItem('policy-file') || '{}');
        const currentPolicyDiagramId = policyDiagramId || file?.policy_diagram_id;
        if (!currentPolicyDiagramId) {
            return false;
        }

        const store = readBuilderStore();
        const currentSections = Array.isArray(store[currentPolicyDiagramId])
            ? store[currentPolicyDiagramId].map((section) => ({
                ...section,
                profiles: Array.isArray(section?.profiles) ? [...section.profiles] : [],
            }))
            : null;

        if (!currentSections) {
            return false;
        }

        const targetSectionIndex = currentSections.findIndex((section) => section?.dataKey === event.dataKey);
        if (targetSectionIndex < 0) {
            return false;
        }

        const payload = sanitizeBuilderProfilePayload(data, event.dataKey);

        if (event.action === 'update') {
            const targetId = payload?.profile_id || payload?.id;
            if (!targetId) {
                return false;
            }

            const profileIndex = currentSections[targetSectionIndex].profiles.findIndex((profile) => {
                const profileId = profile?.profile_id || profile?.id;
                return profileId === targetId;
            });

            if (profileIndex < 0) {
                return false;
            }

            currentSections[targetSectionIndex].profiles[profileIndex] = {
                ...currentSections[targetSectionIndex].profiles[profileIndex],
                ...payload,
            };

            if (event.dataKey === 'application') {
                delete currentSections[targetSectionIndex].profiles[profileIndex].action;
            }
        } else {
            currentSections[targetSectionIndex].profiles.push(payload);
        }

        const nextStore = {
            ...store,
            [currentPolicyDiagramId]: currentSections,
        };

        localStorage.setItem(POLICY_PROFILE_BUILDER_STORAGE_KEY, JSON.stringify(nextStore));
        setBuilderStore(nextStore);
        window.dispatchEvent(new CustomEvent(PROFILE_BUILDER_CHANGED_EVENT));
        return true;
    };

    return (
        <Box
            sx={{
                height: '100%',
                width: '270px',
                backgroundColor: '#fff',
                borderRight: '1px solid #ccc',
                overflow: 'hidden',
            }}
        >
            <PanelGroup direction="vertical" className="h-full min-h-0">
                {hasUsedSubjectAttributes && (
                    <Panel id="profile" order={1} defaultSize={40} minSize={20} className="flex flex-col min-w-0 min-h-0">
                        <Box sx={{ pt: '14px', pl: '14px', pr: '10px', pb: '8px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="subtitle2" sx={{ m: 0, lineHeight: 1.2 }}>
                                    Simulation Profile
                                </Typography>
                                <Tooltip title="Profile Management" arrow>
                                    <TuneIcon sx={{ fontSize: 22 }} className='text-gray-400 p-0.5 hover:text-blue-600 cursor-pointer' onClick={handleOpenProfileManagement} />
                                </Tooltip>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Please select subject profile
                            </Typography>
                        </Box>
                        <Box className="flex-1 w-full overflow-auto bg-white border-collapse">
                            <CustomProfilesPolicySimulator
                                profiles={profiles}
                                onProfileChanged={(isModified) => onProfileChanged(isModified)}
                                onSelectedProfile={(profile) => onSelectedProfile(profile)}
                                onSaveProfile={(profile) => onSaveProfile(profile)}
                                handleEditProfile={(profile) => handleEditProfile(profile)}
                                handleDeleteProfile={(profile) => handleDeleteProfile(profile)}
                                nodes={nodes}
                            // runSimulatePolicy={(profile) => runSimulatePolicy(profile)}
                            />
                        </Box>
                    </Panel>
                )}

                {hasUsedSubjectAttributes && <PanelResizeHandle className='h-0.5 w-full bg-slate-300 hover:bg-slate-400' />}

                <Panel id="environment" order={2} minSize={hasUsedSubjectAttributes ? 25 : 100} className="flex flex-col min-w-0 min-h-0 overflow-hidden">
                    <Box sx={{ pt: '14px', pl: '14px', pr: '10px', pb: '8px' }}>
                        <Typography variant="subtitle2" sx={{ m: 0, lineHeight: 1.2 }}>
                            Simulation Profile Builder
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                            {currentPolicyBuilderSections.map((section) => {
                                const sectionKey = section.dataKey;
                                const isSectionEnabled = sectionAvailability[sectionKey] !== false;
                                const expanded = isSectionEnabled ? (expandedSections[sectionKey] ?? true) : false;
                                const sectionItems = Array.isArray(section?.profiles) ? section.profiles : [];

                                return (
                                    <Box
                                        key={sectionKey}
                                        sx={{
                                            border: '1px solid #ddd',
                                            borderRadius: 1,
                                            my: 1.5,
                                            overflow: 'hidden',
                                            mx: 2
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                px: 1,
                                                py: 0.75,
                                                pr: 0.2,
                                                backgroundColor: '#f8f8f8',
                                                borderBottom: expanded ? '1px solid #eee' : 'none',
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                {section.label}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <IconButton
                                                    size="small"
                                                    sx={{ p: 0, fontSize: 20 }}
                                                    disabled={!isSectionEnabled}
                                                    onClick={() => handleProfileBuilderEvent(section.label, 'create', section.dataKey)}
                                                >
                                                    <AddCircleIcon
                                                        fontSize="inherit"
                                                        sx={{
                                                            color: isSectionEnabled ? '#9ca3af' : '#d1d5db',
                                                            '&:hover': {
                                                                color: isSectionEnabled ? '#2563eb' : '#d1d5db',
                                                            },
                                                        }}
                                                    />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    aria-label={`toggle ${section.label}`}
                                                    disabled={!isSectionEnabled}
                                                    onClick={() => {
                                                        if (isSectionEnabled) {
                                                            toggleSection(sectionKey);
                                                        }
                                                    }}
                                                >
                                                    {expanded ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />}
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        {expanded && (
                                            <List disablePadding>
                                                {sectionItems.length === 0 && (
                                                    <ListItem sx={{ px: 1, py: 0.5 }}>
                                                        <ListItemText
                                                            primary="No profiles"
                                                            primaryTypographyProps={{
                                                                fontSize: 12,
                                                                color: 'text.secondary',
                                                            }}
                                                        />
                                                    </ListItem>
                                                )}

                                                {sectionItems.map((item) => (
                                                    <ListItem
                                                        key={`${sectionKey}-${item.profile_id || item.id || item.profile_name}`}
                                                        disableGutters
                                                        secondaryAction={
                                                            <IconButton
                                                                edge="end"
                                                                size="small"
                                                                aria-label={`options ${item.profile_name}`}
                                                                sx={{ mr: 0.2 }}
                                                                onClick={(event) => handleOpenBuilderItemMenu(event, section, item)}
                                                            >
                                                                <MoreVertIcon fontSize="inherit" />
                                                            </IconButton>
                                                        }
                                                        sx={{ px: 1, py: 0.5 }}
                                                    >
                                                        <ListItemText
                                                            primary={item.profile_name || '-'}
                                                            primaryTypographyProps={{
                                                                fontSize: 12,
                                                                noWrap: true,
                                                            }}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Panel>
            </PanelGroup>
            <UserProfileManagement
                open={openManagement}
                setOpen={setOpenManagement}
                project={project}
                profiles={simulatorProfiles}
                onSaveProfile={(profile) => onSaveProfile(profile)}
                usedSubjectAttributes={memoizedAttributes}
                onAddNewProfile={onAddNewProfile}
                onCloneProfile={(profile) => onCloneProfile(profile)}
                selectedEditProfile={selectedEditProfile}
                initialProfileId={selectedEditProfile?.id}
            />
            <ConfirmDialogReuse
                open={openDeleteConfirm}
                maxWidth={"sm"}
                content={
                    <div>
                        <Typography variant="subtitle1">Are you sure you want to delete profile "{selectedDeleteProfile?.name}" ?</Typography>
                    </div>
                }
                actions={
                    <div className='grid grid-cols-2 gap-2'>
                        <LoadingButton
                            variant="contained"
                            loading={loading}
                            onClick={() => handleDelete()}
                        >
                            Confirm
                        </LoadingButton>
                        <Button
                            disabled={loading}
                            variant="outlined"
                            onClick={() => onDeleteClose()}
                        >
                            Cancel
                        </Button>
                    </div>
                }
            />
            <ProfileBuilderManagement
                open={openProfileBuilderManagement}
                setOpen={setOpenProfileBuilderManagement}
                event={eventProfileBuilder}
                onSubmit={onSubmitProfileBuilder}
            />
            <Menu
                anchorEl={builderItemMenuAnchorEl}
                open={Boolean(builderItemMenuAnchorEl)}
                onClose={handleCloseBuilderItemMenu}
            >
                <MenuItem onClick={handleEditBuilderItem}>Edit</MenuItem>
                <MenuItem onClick={handleDeleteBuilderItem}>Delete</MenuItem>
            </Menu>
        </Box>
    );
};